import { useCallback, useState } from 'react';
import { AxiosError } from 'axios';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { VStack } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import { useProject } from ':cloud/contexts/ProjectContext';
import useProjectNavigation from ':cloud/hooks/useProjectNavigation';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useCreateSmartContracts } from ':cloud/queries/SmartContractQueries/useCreateSmartContracts';
import { useDeleteSmartContract } from ':cloud/queries/SmartContractQueries/useDeleteSmartContracts';
import { useGetSmartContracts } from ':cloud/queries/SmartContractQueries/useGetSmartContracts';
import { useUpdateSmartContracts } from ':cloud/queries/SmartContractQueries/useUpdateSmartContracts';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { CoinbaseSmartContract } from ':cloud/types/ts_types';
import { Layout } from ':cloud/widgets/layout';
import { MAIN_CONTAINER_MIN_WIDTH } from ':cloud/widgets/product/constants';
import { CloudErrorBoundary } from ':cloud/widgets/sharedcomponents';
import { CoinbaseSmartContractEditModal } from ':cloud/widgets/smartContracts/CoinbaseSmartContractEditModal';
import { CoinbaseSmartContractRegisterModal } from ':cloud/widgets/smartContracts/CoinbaseSmartContractRegisterModal';
import { CoinbaseSmartContractTable } from ':cloud/widgets/smartContracts/CoinbaseSmartContractTable';

function SmartContractsContent(): JSX.Element {
  const [
    registerModalVisible,
    { toggleOn: toggleRegisterModalOn, toggleOff: toggleRegisterModalOff },
  ] = useToggler(false);

  const [editModalVisible, { toggleOn: toggleEditModalOn, toggleOff: toggleEditModalOff }] =
    useToggler(false);

  const [selectedSmartContract, setSelectedSmartContract] = useState<CoinbaseSmartContract | null>(
    null,
  );
  const { activeOrg } = useGetUser();
  const { selectedProject } = useProject();
  const toast = useToast();

  const { data: smartContractsData, isLoading: isSmartContractsLoading } = useGetSmartContracts(
    selectedProject?.organizationId ?? '',
    selectedProject?.id ?? '',
  );

  const createSmartContractMutation = useCreateSmartContracts();
  const updateSmartContractMutation = useUpdateSmartContracts();

  const deleteSmartContractMutation = useDeleteSmartContract((deletedSmartContractId) => {
    toast.show(`Smart Contract ${deletedSmartContractId} deleted`, {
      variant: 'positive',
    });
  });

  const showRequestError = useCallback(
    (e) => {
      let error = 'Something went wrong';
      if (e instanceof AxiosError && e?.response?.data.message) {
        error += `: ${e.response.data.message}`;
      }
      toast.show(error, {
        variant: 'negative',
      });
    },
    [toast],
  );

  const handleRegisterSmartContract = useCallback(
    (smartContract: CoinbaseSmartContract) => {
      createSmartContractMutation.mutate(
        {
          orgId: activeOrg?.organizationId || '',
          projectId: selectedProject?.id || '',
          smartContract,
        },
        {
          onSuccess: (upsertedSmartContract) => {
            toast.show(`Smart Contract ${upsertedSmartContract?.contractAddress} registered`, {
              variant: 'positive',
            });
            toggleRegisterModalOff();
          },
          onError: showRequestError,
        },
      );
    },
    [
      createSmartContractMutation,
      activeOrg?.organizationId,
      selectedProject?.id,
      showRequestError,
      toast,
      toggleRegisterModalOff,
    ],
  );

  const handleUpdateSmartContract = useCallback(
    (smartContract: CoinbaseSmartContract) => {
      updateSmartContractMutation.mutate(
        {
          orgId: activeOrg?.organizationId || '',
          projectId: selectedProject?.id || '',
          smartContract,
        },
        {
          onSuccess: () => {
            toggleEditModalOff();
          },
          onError: showRequestError,
        },
      );
    },
    [
      updateSmartContractMutation,
      activeOrg?.organizationId,
      selectedProject?.id,
      showRequestError,
      toggleEditModalOff,
    ],
  );

  const handleDeleteSmartContract = useCallback(
    (smartContract: CoinbaseSmartContract) => {
      deleteSmartContractMutation.mutate({
        orgId: activeOrg?.organizationId || '',
        projectId: selectedProject?.id || '',
        smartContractId: smartContract.smartContractId || '',
      });
    },
    [selectedProject, deleteSmartContractMutation, activeOrg?.organizationId],
  );

  const handleRegisterSmartContractPress = useCallback(() => {
    logClick(EventName.create_smart_contract_click);

    toggleRegisterModalOn();
  }, [toggleRegisterModalOn]);

  const handleEditSmartContractPress = useCallback(
    (smartContract) => {
      setSelectedSmartContract(smartContract);
      toggleEditModalOn();
    },
    [toggleEditModalOn],
  );

  const handleEditPressRegisterModal = (contract: CoinbaseSmartContract | null) => {
    toggleRegisterModalOff();
    handleEditSmartContractPress(contract);
  };

  return (
    <>
      {registerModalVisible && (
        <CoinbaseSmartContractRegisterModal
          onSubmit={handleRegisterSmartContract}
          onEditPress={handleEditPressRegisterModal}
          onRequestClose={toggleRegisterModalOff}
          smartContractsData={smartContractsData}
        />
      )}

      {editModalVisible && selectedSmartContract ? (
        <CoinbaseSmartContractEditModal
          onRequestClose={toggleEditModalOff}
          smartContract={selectedSmartContract}
          onSubmit={handleUpdateSmartContract}
          onDelete={handleDeleteSmartContract}
        />
      ) : null}

      <VStack gap={8}>
        <CoinbaseSmartContractTable
          smartContractsData={smartContractsData}
          isSmartContractsLoading={isSmartContractsLoading}
          onCreatePress={handleRegisterSmartContractPress}
          onEditPress={handleEditSmartContractPress}
        />
      </VStack>
    </>
  );
}

export function SmartContracts() {
  useProjectNavigation();
  return (
    <Layout>
      <Layout.MainContainer fullWidth minWidth={MAIN_CONTAINER_MIN_WIDTH}>
        <CloudErrorBoundary name="SmartContractsTable" isComponentLevel>
          <SmartContractsContent />
        </CloudErrorBoundary>
      </Layout.MainContainer>
    </Layout>
  );
}
